import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { AssetsById } from '@/api/handlers/resources/fixedAssets';
import type {
    CreateFixedAssetRequest,
    DepreciationRunRequest,
    DisposalRequest,
    UpdateFixedAssetRequest,
} from '@/api/handlers/resources/fixedAssets/requests';
import type {
    CommitDepreciationRunResponse,
    CommitDisposalResponse,
    CreateFixedAssetResponse,
    PreviewDepreciationRunResponse,
    PreviewDisposalResponse,
    UpdateFixedAssetResponse,
} from '@/api/handlers/resources/fixedAssets/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import type { JournalEntry } from '@/api/models';
import { AccountQueryKeys } from '../accounts/accountQueryKeys';
import { JournalQueryKeys } from '../journal/journalQueryKeys';
import { FixedAssetQueryKeys } from './fixedAssetQueryKeys';

/**
 * What a call that **persists a draft** moves.
 *
 * Not the same set as the mock era's `invalidatePostedEntry`, and the difference
 * is the whole shape of this integration: a commit here creates a DRAFT, and a
 * draft moves **no balances**. So:
 *
 * - the **journal** refetches — a new draft entry exists and belongs in the list;
 * - the **register** refetches — a created asset is in it immediately, and a
 *   committed run now has an `existingRun` a second preview must surface;
 * - the **accounts** do *not*, because nothing has posted. Invalidating them
 *   would suggest the balances had moved when they had not.
 *
 * The reconciliation is included because the register side of it can move
 * without the ledger side: creating an asset whose acquisition entry is still a
 * draft makes the tie-out report a real, correct difference, and the screen has
 * to show that rather than a stale agreement.
 */
const invalidateDraftWrite = (queryClient: QueryClient): void => {
    queryClient.invalidateQueries({ queryKey: FixedAssetQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: JournalQueryKeys.all });
};

/**
 * Create an asset.
 *
 * On an acquisition the response carries a DRAFT entry to hand to the journal
 * form; on an opening asset `draft` is null and nothing posts — which is the
 * point of the distinction, not an edge case.
 */
export const useCreateFixedAssetMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<CreateFixedAssetResponse>,
        Error,
        CreateFixedAssetRequest
    >({
        mutationFn: (body) => apiClient.fixedAssets.createAsset(body),
        onSuccess: (result) => {
            if (result.success) invalidateDraftWrite(queryClient);
        },
    });
};

/** Edit an asset. Touches no ledger account and builds no entry. */
export const useUpdateFixedAssetMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<UpdateFixedAssetResponse>,
        Error,
        { assetId: string; body: UpdateFixedAssetRequest }
    >({
        mutationFn: ({ assetId, body }) =>
            apiClient.fixedAssets.updateAsset(assetId, body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: FixedAssetQueryKeys.all,
                });
            }
        },
    });
};

/**
 * Compute a disposal's gain or loss and build its unsaved draft. Writes nothing
 * and invalidates nothing.
 */
export const usePreviewDisposalMutation = () => {
    const { apiClient } = useApi();

    return useMutation<
        ApiResponse<PreviewDisposalResponse>,
        Error,
        { assetId: string; body: DisposalRequest }
    >({
        mutationFn: ({ assetId, body }) =>
            apiClient.fixedAssets.previewDisposal(assetId, body),
    });
};

/**
 * Record the disposal terms and persist its DRAFT entry.
 *
 * **The asset stays `ACTIVE`.** It is disposed when the entry posts, not here —
 * so nothing in this mutation may optimistically mark it otherwise.
 */
export const useCommitDisposalMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<CommitDisposalResponse>,
        Error,
        { assetId: string; body: DisposalRequest }
    >({
        mutationFn: ({ assetId, body }) =>
            apiClient.fixedAssets.commitDisposal(assetId, body),
        onSuccess: (result) => {
            if (result.success) invalidateDraftWrite(queryClient);
        },
    });
};

/**
 * Preview a period's depreciation: per-asset charges, the total, and one
 * balanced unsaved draft. Pure — it never writes and never invalidates.
 *
 * `assetsById` is the register the caller already holds; it is what lets a
 * `CATCH_UP` line report how many instalments it is charging, which the API does
 * not serve.
 */
export const usePreviewDepreciationRunMutation = () => {
    const { apiClient } = useApi();

    return useMutation<
        ApiResponse<PreviewDepreciationRunResponse>,
        Error,
        { body: DepreciationRunRequest; assetsById?: AssetsById }
    >({
        mutationFn: ({ body, assetsById }) =>
            apiClient.fixedAssets.previewDepreciationRun(body, assetsById),
    });
};

/**
 * Persist the run and its DRAFT entry.
 *
 * Idempotent per period — a second commit returns `alreadyExisted: true` and the
 * same entry, guarded server-side by a unique index rather than by anything here.
 */
export const useCommitDepreciationRunMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<CommitDepreciationRunResponse>,
        Error,
        { body: DepreciationRunRequest; assetsById?: AssetsById }
    >({
        mutationFn: ({ body, assetsById }) =>
            apiClient.fixedAssets.commitDepreciationRun(body, assetsById),
        onSuccess: (result) => {
            if (result.success) invalidateDraftWrite(queryClient);
        },
    });
};

/**
 * What a **posted** asset entry moves — and why this is separate from
 * {@link invalidateDraftWrite}.
 *
 * A commit here creates a DRAFT, which moves no balances. The register position,
 * the account balances and the tie-out all move when a person **posts** that
 * draft in the journal — and `usePostJournalEntryMutation` invalidates only the
 * journal, because posting is a journal concern and it has no reason to know
 * which subsidiary register built the entry.
 *
 * So the register has to react to the post itself. Without this, posting a
 * depreciation run or a disposal leaves the totals, the asset's status and the
 * reconciliation showing their **pre-post** state until something incidental
 * refetches — a tie-out reporting a difference that has already been cleared,
 * which is precisely the failure the tie-out exists to catch.
 */
export const useInvalidatePostedAssetEntry = () => {
    const queryClient = useQueryClient();

    return React.useCallback(
        (entry: Pick<JournalEntry, 'status'> | null | undefined) => {
            // A save that did not post moves nothing: the draft already existed.
            if (entry?.status !== 'POSTED') return;
            queryClient.invalidateQueries({
                queryKey: FixedAssetQueryKeys.all,
            });
            queryClient.invalidateQueries({ queryKey: JournalQueryKeys.all });
            // The balances the tie-out's ledger side is computed from.
            queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
        },
        [queryClient],
    );
};
